import React, { useContext, useRef, useState, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { requestCreateCriterionC } from 'api/criterionC'
import { requestEcoRegionsSearch } from 'api/ecoRegions'
import CheckIcon from 'assets/icons/check-icon'
import Button from 'components/Button'
import Col from 'components/Col'
import Container from 'components/Container'
import Input from 'components/Input'
import MultiSelectionDropdown from 'components/multiSelectDropdown'
import Row from 'components/Row'
import SearchDropdown from 'components/SearchDropdown'
import Textarea from 'components/Textarea'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import { reasonDefaultOptions } from 'constants/rejectTriggerReason'
import {
  requestUpdateCriterionCFiles,
  requestDeleteCriterionCFiles,
  requestGetCriterionCFiles,
} from 'api/site'
import Uploader from 'flow/siteProposal/pages/about/uploader'
import UploadSiteFiles from 'components/SitesUploads'
import { showErrorToast } from 'utils/toast'

import { 
  SITE_STATUS_DRAFT_ID, 
  SITE_STATUS_EDIT_ONLY_ID, 
  SITE_STATUS_SUBMITTED_EDIT_ONLY_ID,
  SITE_STATUS_CONFIRMED_ID,
  SITE_STATUS_SUPERSEDED_ID,
  SITE_STATUS_DELISTED_ID
} from 'constants/siteStatuses'
import { SiteContext } from 'providers/site'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'
import { delay } from 'utils/delay'
import { EcosystemContentFooter } from '../ecosystemData/style'
import { hasQualifyingReason, mapTriggerReasonFromApi } from 'utils/criterionCHelpers'
import { normFile, uniqByKey, extractFiles } from 'utils/fileUploadHelpers'
import { ProfileContext } from 'providers/profile'
import { canAccessBlobExplorer } from 'utils/rolePermissions'
import {
  findHistoryId,
  findRawSiteId,
  findSiteId,
  getCriterionCEntityId,
  fetchCriterionCFilesByScope,
  createCriterionCHandlers,
  createCriterionCUploaderHandlers,
  getSelectedSite,
  getEcoRegionOptions,
  resolveCriterionCCheckFields,
  setDisabled,
  setDisabledQualificationReason,
  setShowReassessmentFields,
  setShowFinishButton,
  hasTriggerReasons
} from 'helpers/criterionC/accordionContent'


const inputSaveDelayTime = 300
const MIN_VALID_YEAR = 1900
const YEAR_DIGIT_COUNT = 4
const updateEcoRegionQueryTimeOut = delay(inputSaveDelayTime)
const updateEcologicalIntegrityEcoRegionTimeOut = delay(inputSaveDelayTime)
const updateNumberOfCriterionSitesInEcoRegionTimeOut = delay(inputSaveDelayTime)
const updateEvidenceOfLowHumanImpactTimeOut = delay(inputSaveDelayTime)
const updateEvidenceForIntactEcologicalCommunitiesTimeOut = delay(inputSaveDelayTime)
const updateDateOfAssesmentTimeOut = delay(inputSaveDelayTime)
const updateTriggerReasonIdTimeOut = delay(inputSaveDelayTime)
const updateTriggerReasonTextTimeOut = delay(inputSaveDelayTime)

const AccordionContent = ({ site, item: criterionC, updateCriterionC, siteId: propSiteId, toggleAccordion, defaultPage = false }) => {
  const params = useParams()
  const checkedValue = useRef([])
  const { siteProposalFieldValidation, isEditable } = useContext(SiteContext)
  const { profile } = useContext(ProfileContext) || {}
  const [checkedOptions, setCheckedOptions] = useState(reasonDefaultOptions)
  const [uploadedFiles, setUploadedFiles] = useState([])

  // Derive siteId from props or site object - ensure it's a primitive value
  const rawSiteId = findRawSiteId(site, propSiteId, params)
  const siteId = findSiteId(rawSiteId)

  const editOnlySiteStatus = site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID || site.SiteStatusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
  const historyId = findHistoryId(site, editOnlySiteStatus, params)
  const isReassessment = Boolean(historyId)
  const criterionCId = getCriterionCEntityId(criterionC)
  const hasPersistedCriterionC = Boolean(criterionCId)

  const refreshCriterionCFiles = useCallback(async () => {
    if (!siteId || !criterionCId) return
    try {
      const res = await fetchCriterionCFilesByScope({ siteId, historyId, isReassessment, requestGetCriterionCFiles })
      const serverFiles = extractFiles(res).map(normFile)
      setUploadedFiles(uniqByKey(serverFiles))
    } catch (error) {
      console.error('Failed to load criterion C files:', error)
    }
  }, [siteId, historyId, criterionCId, isReassessment])

  // Load criterion C files
  useEffect(() => {
    refreshCriterionCFiles()
  }, [refreshCriterionCFiles])

  const uploaderFiles = useMemo(() => (uploadedFiles || []).map((f) => normFile(f)), [uploadedFiles])
  const uploaderParams = useMemo(() => ({ id: siteId, siteHistoryId: historyId }), [siteId, historyId])

  // Show uploader only when there's at least one criterion C (has an ID)
  const hasCriterionC = hasPersistedCriterionC

  const checkFields = resolveCriterionCCheckFields(
    siteProposalFieldValidation,
    criterionC,
    defaultSiteProposalValidationResults.criterionC
  )
  
  const [ecoRegion, setEcoRegion] = useState(
    getSelectedSite(criterionC)
  )
  const [ecoRegionOptions, setEcoRegionOptions] = useState([])
  const [ecoRegionQuery, setEcoRegionQuery] = useState('')
  const [ecoRegionSite, setEcoRegionSite] = useState(criterionC.ecoregion_c_sites_count || '')
  const [ecoIntegrity, setEcoIntegrity] = useState(criterionC.evidence_low_human_impact || '')
  const [ecoIntegrity2, setEcoIntegrity2] = useState(criterionC.evidence_intact_ecology || '')
  const [year, setYear] = useState(criterionC.year || '')
  const [qualificationReason, setQualificationReason] = useState(mapTriggerReasonFromApi(criterionC.trigger_reason_id))

  const [reassessmentRational, setReassessmentRational] = useState(criterionC.trigger_reason_text ? criterionC.trigger_reason_text : '')
  const qualified = hasQualifyingReason(criterionC.trigger_reason_id)

  // Uploader visibility and editability:
  // - Visible in the assessment workflow only up to confirmation.
  // - Editable: Only in DRAFT (when editable) or during reassessment (EDIT_ONLY_ID, SUBMITTED_EDIT_ONLY_ID)
  // - Read-only: In other pre‑confirmation statuses.
  // - After confirmation (and later statuses), the uploader is hidden in this flow so
  //   ecological integrity uploads are not shown here for confirmed sites.
  const isEditableStatus = site.SiteStatusId === SITE_STATUS_DRAFT_ID || 
                          site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID || 
                          site.SiteStatusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
  const isConfirmedOrLater = site.SiteStatusId === SITE_STATUS_CONFIRMED_ID || 
                            site.SiteStatusId === SITE_STATUS_SUPERSEDED_ID || 
                            site.SiteStatusId === SITE_STATUS_DELISTED_ID
  const roleId = profile?.Role?.id ?? profile?.RoleId
  const isKbaSecOrRfp = canAccessBlobExplorer(roleId)
  const disabled = setDisabled(site, qualified, isEditableStatus, isEditable)
  const disabledQualificationReason = setDisabledQualificationReason(site, isEditableStatus, isEditable)
  // Hide downloads together with the uploader after confirmation in this workflow
  const hideDownload = isConfirmedOrLater
  const showReassessmentFields = setShowReassessmentFields(criterionC, site, defaultPage)
  const showFinishButton = setShowFinishButton(site, defaultPage)
  const requireReasonsToFinish = Boolean(criterionC.last_reassessment_date)

  const persistCriterionCField = useCallback(async (payload, delayedFn) => {
    if (!hasPersistedCriterionC) {
      const createdCriterionC = await requestCreateCriterionC(siteId, params.siteHistoryId)
      await updateCriterionC({ criterionCId: getCriterionCEntityId(createdCriterionC), ...payload })
      return
    }

    const run = async () => {
      await updateCriterionC({ criterionCId, ...payload })
    }

    if (delayedFn) {
      delayedFn(run)
      return
    }

    await run()
  }, [hasPersistedCriterionC, siteId, params.siteHistoryId, updateCriterionC, criterionCId])

  const {
    handleChangeValueChecked,
    handleChangeEcoRegionQuery,
    handleChangeEcoRegion,
    handleChangeEcoRegionSite,
    handleChangeEcoIntegrity,
    handleChangeEcoIntegrity2,
    handleChangeYear,
    handleChangeQualificationReason,
    handleChangeReassessmentRational
  } = useMemo(() => createCriterionCHandlers({
    persistCriterionCField,
    setEcoRegionQuery,
    setEcoRegionOptions,
    setEcoRegion,
    setEcoRegionSite,
    setEcoIntegrity,
    setEcoIntegrity2,
    setYear,
    setQualificationReason,
    setReassessmentRational,
    setCheckedOptions,
    checkedValue,
    updateEcoRegionQueryTimeOut,
    updateEcologicalIntegrityEcoRegionTimeOut,
    updateNumberOfCriterionSitesInEcoRegionTimeOut,
    updateEvidenceOfLowHumanImpactTimeOut,
    updateEvidenceForIntactEcologicalCommunitiesTimeOut,
    updateDateOfAssesmentTimeOut,
    updateTriggerReasonIdTimeOut,
    updateTriggerReasonTextTimeOut,
    getEcoRegionOptions: (query) => getEcoRegionOptions(query, requestEcoRegionsSearch),
    minValidYear: MIN_VALID_YEAR,
    yearDigitCount: YEAR_DIGIT_COUNT
  }), [persistCriterionCField])

  const {
    fetchUploaderFiles,
    uploadCriterionCFile: uploaderUploadCriterionCFile,
    deleteCriterionCFile: uploaderDeleteCriterionCFile
  } = useMemo(() => createCriterionCUploaderHandlers({
    rawSiteId,
    historyId,
    isReassessment,
    requestGetCriterionCFiles,
    requestUpdateCriterionCFiles,
    requestDeleteCriterionCFiles,
    extractFiles,
    normFile,
    uniqByKey,
    setUploadedFiles
  }), [rawSiteId, historyId, isReassessment])

  const uploadCriterionCFile = useCallback(async (uploadSiteId, uploadSiteHistoryId, formData, options) => {
    try {
      return await uploaderUploadCriterionCFile(uploadSiteId, uploadSiteHistoryId, formData, options)
    } catch (error) {
      showErrorToast(<FormattedMessage id="uploadFailed" />)
      throw error
    }
  }, [uploaderUploadCriterionCFile])

  const deleteCriterionCFile = useCallback(async (deleteSiteId, deleteSiteHistoryId, fileId) => {
    try {
      return await uploaderDeleteCriterionCFile(deleteSiteId, deleteSiteHistoryId, fileId)
    } catch (error) {
      showErrorToast(<FormattedMessage id="deleteFailed" />)
      throw error
    }
  }, [uploaderDeleteCriterionCFile])

  return (
    <Container width='100%' padding='0px'>
      <div data-testid='criterionC-form'>
        <Row>
          <Col md={6}>
            <SearchDropdown
              value={ecoRegion}
              onChange={handleChangeEcoRegion}
              options={ecoRegionQuery ? ecoRegionOptions : []}
              label='ecoRegion'
              field='ecoRegion'
              height='48px'
              iconTopPosition={checkFields.ecoregion_id ? '-68px' : '-95px'}
              placeholder='searchEcoRegionName'
              dataTest='search-dropdown'
              infoTooltip='ecoRegionHint'
              buttonIsPresent={false}
              indicatorSize='big'
              inputValue={ecoRegionQuery}
              onInputChange={handleChangeEcoRegionQuery}
              inputMargin='12px 0 12px 16px'
              disabled={disabled || !qualified}
              error={
                checkFields.ecoregion_id
                  ? { field: '', error: '' }
                  : {
                    field: 'ecoRegion',
                    error: 'pleaseCompleteThisField'
                  }
              }
              hyper={'https://www.keybiodiversityareas.org/working-with-kbas/programme/regional-focal-points'}
              shortLink={'www.keybiodiversityareas.org'}
            />
          </Col>
          <Col md={6}>
            <Input
              value={ecoRegionSite}
              onChange={handleChangeEcoRegionSite}
              infoTooltip='ecoRegionSiteHint'
              placeholder='enterNumberOfCriterionCSites'
              label='ecoRegionSite'
              field='numberOfCriterionSitesInEcoRegion'
              disabled={disabled || !qualified}
              error={
                checkFields.ecoregion_c_sites_count
                  ? { field: '', error: '' }
                  : {
                    field: 'numberOfCriterionSitesInEcoRegion',
                    error: 'pleaseCompleteThisField'
                  }
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={6} >
            <Textarea
              value={ecoIntegrity2}
              onChange={handleChangeEcoIntegrity2}
              label='ecoIntegrityEvidenceForIntact'
              field='evidenceForIntactEcologicalCommunities'
              infoTooltip='ecoIntegrityIntact'
              placeholder='enterEvidenceForIntactEcologicalCommunities'
              height='148px'
              disabled={disabled || !qualified}
              error={
                checkFields.evidence_intact_ecology
                  ? { field: '', error: '' }
                  : {
                    field: 'evidenceForIntactEcologicalCommunities',
                    error: 'pleaseCompleteThisField'
                  }
              }
            />
          </Col>
          <Col md={6} >
            <Textarea
              value={ecoIntegrity}
              onChange={handleChangeEcoIntegrity}
              label='ecoIntegrityLowHumanImpact'
              field='evidenceOfLowHumanImpact'
              infoTooltip='ecoIntegrityHuman'
              placeholder='enterEvidenceForLowHumanImpact'
              height='148px'
              disabled={disabled || !qualified}
              error={
                checkFields.evidence_low_human_impact
                  ? { field: '', error: '' }
                  : {
                    field: 'evidenceOfLowHumanImpact',
                    error: 'pleaseCompleteThisField'
                  }
              }
            />
          </Col>
        </Row>
        <Row>
          {showReassessmentFields && (
            <Col md={6}>
              <MultiSelectionDropdown
                value={qualificationReason}
                saveChanges={handleChangeQualificationReason}
                valueChecked={handleChangeValueChecked}
                label='triggerEcosystemLabel'
                infoTooltip='triggerLabelHint'
                field='qualificationReason'
                error={qualificationReason?.length === 0 ? {
                  field: 'qualificationReason',
                  error: 'pleaseCompleteThisField'
                } : { field: '', error: '' }}
                secondStepOptions={checkedOptions}
                placeholder='criterionCTriggerPlaceholder'
              />
            </Col>
          )}
          {showReassessmentFields && (
            <Col md={6}>
              <Textarea
                value={reassessmentRational}
                onChange={handleChangeReassessmentRational}
                placeholder='justificationNoChangeCriterionCPlaceholder'
                label='justificationNoChange'
                infoTooltip='qualificationReasonHint'
                field='rationalForReassessment'
                height='128px'
                disabled={disabledQualificationReason}
              />
            </Col>
          )}
        </Row>
        <Row>
          <Col md={6}>
            <Input
              value={year}
              onChange={handleChangeYear}
              infoTooltip='assessmentDateHint'
              placeholder='assessmentDateEx'
              label='assessmentDate'
              field='dateOfAssesment'
              disabled={disabled || !qualified}
              error={
                checkFields.year
                  ? { field: '', error: '' }
                  : {
                    field: 'dateOfAssesment',
                    error: 'pleaseCompleteThisField'
                  }
              }
            />
          </Col>
        </Row>

        {hasCriterionC && (!isConfirmedOrLater || isKbaSecOrRfp) && (
          <Row>
            <Col md={12} style={{ marginTop: '32px', paddingTop: '24px', paddingBottom: '24px' }}>
              <UploadSiteFiles
                label="uploadCriterionCReport"
                field="uploadCriterionCReport"
                infoTooltip="criterionCUploaderHint"
                height="128px"
                dataTest="uploadCriterionCReport-text-area"
                isOptional={true}
                disabled={disabled || !qualified}
              />

              <div style={{ marginTop: '16px' }}>
                <Uploader
                  key={`criterionC-${siteId}-${historyId ?? "assessment"}-${uploadedFiles?.length}-${uploadedFiles
                    ?.map((f) => f.id || f.blob_name || f.file_name || f.name)
                    .join("|")}`}
                  params={uploaderParams}
                  siteFiles={uploaderFiles}
                  disabled={disabled || !qualified}
                  fileType="criterionC"
                  allowDownloadsWhenConfirmed={isKbaSecOrRfp}
                  hideDownload={hideDownload}
                  fetchFilesFn={fetchUploaderFiles}
                  uploadFn={uploadCriterionCFile}
                  deleteFn={deleteCriterionCFile}
                />
              </div>
            </Col>
          </Row>
        )}

        {(showFinishButton && requireReasonsToFinish) && (
          <Row>
            <Col md={12}>
              <EcosystemContentFooter>
                <Button height='40px' isDisabled={!hasTriggerReasons(criterionC.trigger_reason_id)} onClick={toggleAccordion}
                  type={hasTriggerReasons(criterionC.trigger_reason_id) ? 'primary' : 'disabledPrimary'}>
                  <CheckIcon />
                  <FormattedMessage id='finishEditing' />
                </Button>
              </EcosystemContentFooter>
            </Col>
          </Row>
        )}
        {(showFinishButton && !requireReasonsToFinish) && (
          <Row>
            <Col md={12}>
              <EcosystemContentFooter>
                <Button height='40px' onClick={toggleAccordion}
                  type='primary'>
                  <CheckIcon />
                  <FormattedMessage id='finishEditing' />
                </Button>
              </EcosystemContentFooter>
            </Col>
          </Row>
        )}
      </div>
    </Container>
  )
}

export default AccordionContent

const criterionCItemPropType = PropTypes.shape({
  site_ecointegrity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  site_ecointegrity_history_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ecoregion_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ecoregion_c_sites_count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  evidence_low_human_impact: PropTypes.string,
  evidence_intact_ecology: PropTypes.string,
  trigger_reason_id: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  trigger_reason_text: PropTypes.string,
  reassessment_flag: PropTypes.bool,
  last_reassessment_date: PropTypes.string,
  EcoRegion: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string
  })
})

AccordionContent.propTypes = {
  site: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sitehistoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    SiteStatusId: PropTypes.number.isRequired,
    reassessmentFlag: PropTypes.bool
  }).isRequired,
  item: criterionCItemPropType.isRequired,
  updateCriterionC: PropTypes.func.isRequired,
  siteId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ]),
  toggleAccordion: PropTypes.func,
  defaultPage: PropTypes.bool
}
